import { Button, Group, Loader, Paper, Stack, Switch, Text } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Gift } from "lucide-react";
import { useState } from "react";

import { useGetAppearance } from "../../api/generated/endpoints/appearance/appearance";
import type { AppearanceView } from "../../api/generated/model";
import { CaseDialog } from "../../platform/case/CaseDialog";
import { jackpotCredits } from "../../platform/case/daily-case";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { AccountFrame } from "../../platform/shell/AccountFrame";
import { useThemeChoice } from "../../platform/themes/account";
import { useThemeFamily } from "../../platform/themes/current";
import { FAMILIES, isThemeId } from "../../platform/themes/families";
import classes from "./appearance.module.css";
import { FamilyCard } from "./FamilyCard";

/** Today's free daily case, from the tab as from the header's gift icon (spec web/case-switches). */
function CaseRow({ teamId, view }: { teamId: string; view: AppearanceView }) {
  const [dialog, setDialog] = useState(false);
  const { status } = view.case;
  if (status === "hidden" || status === "none") return null;
  const left = view.themes.filter((theme) => !theme.owned).length;
  const themes = left === 1 ? "the last theme you don't have yet" : `one of the ${left} themes you don't have yet`;
  const jackpot = jackpotCredits(view);
  const credits = jackpot === null ? "" : `, or the jackpot's ${jackpot.toLocaleString("en-US")} free credits`;
  return (
    // The words keep about 160px beside the button; where they cannot (a phone), the button goes
    // under them, at the row's end, rather than squeeze them a word a line.
    <Group className={classes.caseRow}>
      <Gift size={22} className={classes.caseIcon} aria-hidden />
      <Stack gap={2} className={classes.caseText}>
        <Text fw={600} size="sm">
          {status === "waiting" ? "Your free case is ready" : "Today's free case is open"}
        </Text>
        <Text size="xs" c="var(--cai-color-text-muted)">
          {status === "waiting"
            ? `Win ${themes}${credits}.`
            : "Next free case tomorrow."}
        </Text>
      </Stack>
      <Button size="sm" className={classes.caseButton} onClick={() => setDialog(true)}>
        {status === "waiting" ? "Open your case" : "See today's"}
      </Button>
      <CaseDialog key={view.case.day} teamId={teamId} view={view} opened={dialog} onClose={() => setDialog(false)} />
    </Group>
  );
}

/**
 * The theme families as cards: an owned one is shown at once and saved to the account; a locked
 * one says it is won in the free daily case. Light or dark stays the header's sun or moon.
 */
function ThemeSection({ teamId, view }: { teamId: string; view: AppearanceView }) {
  const { choose, setCasesHidden, update } = useThemeChoice();
  const shown = useThemeFamily().id;
  const [locked, setLocked] = useState<string | null>(null);
  const entries = new Map(view.themes.map((theme) => [theme.id, theme]));
  return (
    <Paper className={classes.card}>
      <Stack gap={4}>
        <Text className={classes.cardTitle}>Theme</Text>
        <Text className={classes.cardDescription}>
          Only you see it. Light or dark: the sun or moon at the top right, as always.
        </Text>
      </Stack>
      <CaseRow teamId={teamId} view={view} />
      <fieldset className={classes.gallery} aria-label="Themes">
        {FAMILIES.map((family) => {
          const entry = isThemeId(family.id) ? entries.get(family.id) : undefined;
          const owned = entry?.owned ?? false;
          return (
            <FamilyCard
              key={family.id}
              family={family}
              rarity={entry?.rarity ?? "free"}
              owned={owned}
              current={shown === family.id}
              onChoose={() => {
                if (!owned || !isThemeId(family.id)) {
                  setLocked(family.name);
                  return;
                }
                setLocked(null);
                if (family.id !== shown) choose(family.id);
              }}
            />
          );
        })}
      </fieldset>
      {locked === null ? null : (
        <Text component="output" size="sm" c="var(--cai-color-text)">
          {locked} is won in the free daily case.
        </Text>
      )}
      {update.isError ? <ErrorAlert error={update.error} /> : null}
      {view.team_cases_hidden ? (
        <Text size="sm" c="var(--cai-color-text-muted)">
          Your team's admin turned the free daily case off. The themes you won stay yours.
        </Text>
      ) : (
        <Switch
          label="Show the free daily case"
          description="Off hides the case and its gift icon for you. The themes you won stay yours."
          checked={!view.cases_hidden}
          onChange={(event) => setCasesHidden(!event.currentTarget.checked)}
        />
      )}
    </Paper>
  );
}

/** The Account page's Appearance tab (spec web/appearance-tab). */
export function AppearanceScreen() {
  const { teamId } = useParams({ from: "/signed-in/teams/$teamId/account/appearance" });
  return (
    <AccountFrame teamId={teamId}>
      <Appearance teamId={teamId} />
    </AccountFrame>
  );
}

/** The Appearance tab's body, wherever its frame is (Classic's Account page, or Pro's Settings). */
export function Appearance({ teamId }: { teamId: string }) {
  const view = useGetAppearance(teamId);
  return (
    <Stack gap={24}>
      {view.isError ? <ErrorAlert error={view.error} /> : null}
      {view.data === undefined ? (
        view.isError ? null : <Loader size="sm" aria-label="Loading your themes" />
      ) : (
        <ThemeSection teamId={teamId} view={view.data} />
      )}
    </Stack>
  );
}
